import { useMemo } from 'react';
import { type Control, useWatch } from 'react-hook-form';

import { usePermissions } from '@/contexts/auth';
import {
    type OverviewCage,
    type OverviewLocation,
    useGetClientFishGroupsOverview,
} from '@/domain/fishGroups';

import type { FormValues } from './relocateForm';

export interface EmptyCageOption {
    cageId: number;
    cageName: string;
    locationName: string;
}

export interface RelocateCages {
    emptyCages: EmptyCageOption[];
    sourceCage: OverviewCage | null;
    sourceBioscopes: number[];
    /** The cage picked in the form, once one is. */
    targetCage: OverviewCage | null;
    hasBioscopeMovePermission: boolean;
}

/** The cages a fish group can move to from its current one, and what sits in them. */
export function useRelocateCages(
    clientId: number,
    currentCageId: number | null,
    control: Control<FormValues>
): RelocateCages {
    const { data: overview } = useGetClientFishGroupsOverview(clientId);
    const { canMoveBioscopes } = usePermissions();

    // Resolve the source FG's home location once — used both to filter empty
    // cages (relocate is intra-location only) and to pass the locationId
    // when checking the bioscope-move permission.
    const homeLocation = useMemo<OverviewLocation | null>(() => {
        if (!overview) return null;
        const all = [...overview.activeLocations, ...overview.inactiveLocations];
        return (
            all.find((location) => location.cages.some((cage) => cage.cageId === currentCageId)) ??
            null
        );
    }, [overview, currentCageId]);

    const emptyCages = useMemo<EmptyCageOption[]>(() => {
        if (!homeLocation) return [];
        return homeLocation.cages
            .filter((cage) => cage.activeFishGroup === null && cage.cageId !== currentCageId)
            .map((cage) => ({
                cageId: cage.cageId,
                cageName: cage.cageName,
                locationName: homeLocation.locationName,
            }));
    }, [homeLocation, currentCageId]);

    const sourceCage = useMemo<OverviewCage | null>(() => {
        if (!homeLocation || currentCageId == null) return null;
        return homeLocation.cages.find((c) => c.cageId === currentCageId) ?? null;
    }, [homeLocation, currentCageId]);

    const sourceBioscopes = sourceCage?.activeBioscopes ?? [];

    // Watch the target cage selection to surface "current bioscopes at the
    // destination" context inside the bioscopes section — purely informative.
    const watchedTargetCageId = useWatch({ control, name: 'targetCageId' });
    const targetCage = useMemo<OverviewCage | null>(() => {
        if (!homeLocation || !watchedTargetCageId) return null;
        const id = Number(watchedTargetCageId);
        return homeLocation.cages.find((c) => c.cageId === id) ?? null;
    }, [homeLocation, watchedTargetCageId]);

    const hasBioscopeMovePermission = homeLocation
        ? canMoveBioscopes(clientId, homeLocation.locationId)
        : false;

    return { emptyCages, sourceCage, sourceBioscopes, targetCage, hasBioscopeMovePermission };
}
